// ANA-R3-ESC-01 — viewer-surface "escape stack".
//
// One ESC press should interrupt exactly ONE thing — the highest-priority
// interaction that is currently active — and leave everything else alone.
// A second press peels the next layer, and so on, until the lowest level
// (deselecting the active bounding box). This mirrors how a user expects
// ESC to behave: abort the top-most feature first, fall through to a plain
// "deselect everything" only when nothing else is in progress.
//
// Why a coordinator instead of N independent `window` keydown listeners:
// before this module, the marquee-zoom (packages/ui Viewer), the manual
// draw overlay, and the split-view overlay each installed their own global
// ESC listener with no awareness of one another. With a selection present
// AND draw mode on, a single ESC fired *both* — the opposite of "one layer
// per press". Routing every analysis-surface ESC consumer through one
// registry makes the ordering deterministic and additive: a new feature
// registers a layer with a priority and is automatically slotted in.
//
// Scope: the analysis viewer surface only. The registry's window listener
// is installed lazily on the first `registerEscapeLayer` call and removed
// when the last layer unregisters, so it costs nothing outside the
// analysis page. It does NOT preventDefault unless a layer actually
// consumes the press, so ESC stays free for the browser / MUI overlays
// when no viewer layer is active.

/**
 * Priority of each ESC layer — higher numbers are consumed first. Gaps are
 * intentional so future features can slot between existing ones without a
 * renumber. The ordering encodes the product decision (2026-06-09):
 *   split-view (above everything) → marquee → draw → geometry-edit → deselect.
 */
export const ESCAPE_PRIORITY = {
  /** Split-view overlay is open — ESC closes it before any viewer interaction. */
  splitView: 50,
  /** Right-drag zoom marquee in progress — handled by the shared Viewer primitive. */
  marquee: 40,
  /** Manual draw mode placing a new bbox/polygon. */
  draw: 30,
  /** Editing an existing polygon's geometry. Reserved for ANA-R3-GEOM — no
   *  layer registers here yet; the slot exists so that work drops in without
   *  reordering. */
  geometryEdit: 20,
  /** Lowest level — a finding/defect is selected; ESC deselects everything. */
  deselect: 10,
} as const;

export interface EscapeLayer {
  /** Stable id; re-registering the same id replaces the prior layer. */
  id: string;
  /** Higher wins. Use a value from ESCAPE_PRIORITY. */
  priority: number;
  /** Whether this layer currently has something ESC can interrupt. */
  isActive: () => boolean;
  /** Perform the interrupt (close / cancel / deselect). Ignored when passthrough. */
  handle: () => void;
  /**
   * When true and this layer is the top active layer, the coordinator
   * neither calls `handle` nor swallows the event — it simply stops the
   * lower layers from firing and lets the press flow to whoever owns the
   * real handling (the marquee cancel lives inside the shared Viewer
   * primitive, which keeps its own internal listener). Without this, ESC
   * during an active marquee drag would also deselect the bbox.
   */
  passthrough?: boolean;
}

const layers = new Map<string, EscapeLayer>();
let listening = false;

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  return (
    tag === "input" ||
    tag === "textarea" ||
    tag === "select" ||
    target.isContentEditable
  );
}

function onKeyDown(e: KeyboardEvent): void {
  if (e.key !== "Escape") return;
  // Never steal ESC from a field (clearing/blurring an input is its job).
  if (isTypingTarget(e.target)) return;

  let top: EscapeLayer | null = null;
  for (const layer of layers.values()) {
    if (!layer.isActive()) continue;
    if (top === null || layer.priority > top.priority) top = layer;
  }
  if (top === null) return;

  // Passthrough layers (marquee) are owned elsewhere — block the lower
  // layers by virtue of being the top match, but don't consume the event.
  if (top.passthrough) return;

  e.preventDefault();
  e.stopPropagation();
  top.handle();
}

function ensureListening(): void {
  if (listening || typeof window === "undefined") return;
  window.addEventListener("keydown", onKeyDown);
  listening = true;
}

function maybeStopListening(): void {
  if (listening && layers.size === 0 && typeof window !== "undefined") {
    window.removeEventListener("keydown", onKeyDown);
    listening = false;
  }
}

/**
 * Register an ESC layer. Returns an unregister fn — call it on unmount.
 * The window listener is installed on the first registration and torn down
 * when the last layer unregisters.
 */
export function registerEscapeLayer(layer: EscapeLayer): () => void {
  layers.set(layer.id, layer);
  ensureListening();
  return () => {
    // Only delete if still the same layer object (guards against a
    // re-register having replaced it between mount/unmount races).
    if (layers.get(layer.id) === layer) layers.delete(layer.id);
    maybeStopListening();
  };
}

// Test seam — lets unit/Playwright specs introspect the active stack
// without reaching into module internals. Mirrors selectionStore's
// `__wpSelectionStore` convention. Harmless on the production bundle.
interface WindowWithEscapeStack {
  __wpEscapeStack?: {
    /** Ids of currently-active layers, highest priority first. */
    activeIds: () => string[];
  };
}
if (typeof window !== "undefined") {
  (window as unknown as WindowWithEscapeStack).__wpEscapeStack = {
    activeIds: () =>
      [...layers.values()]
        .filter((l) => l.isActive())
        .sort((a, b) => b.priority - a.priority)
        .map((l) => l.id),
  };
}
